import { useCallback, useEffect, useMemo, useState, type MutableRefObject, type ReactNode } from 'react';
import { styled, type SxProps, useTheme } from '@mui/material';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useDeepEqualMemo } from '@/hooks/useDeepEqualMemo';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { CaretIcon } from '../Icons/CaretIcon';
import { EllipsisText } from '../EllipsisText/EllipsisText';
import { BaseSelect, type BaseSingleSelectProps, type Option } from '../BaseSelect/BaseSelect';
import { isOptionLike, type TriggerOptions } from '../BaseSelect/selectUtils';
import { DropdownFpl, type DropdownFplPosition } from './DropdownFpl';
import { DropdownSearchFpl } from './DropdownSearchFpl';
import type { ApiObject } from '@/types/analytics.types';

// Export here for convenience
export type { Option };

interface DropdownButtonProps {
  hasLabel?: boolean;
  isOpen?: boolean;
  width?: string;
  noBorder?: boolean;
}

const DropdownButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'hasLabel' && prop !== 'isOpen' && prop !== 'noBorder' && prop !== 'width',
})<DropdownButtonProps>(({ theme, hasLabel = true, isOpen = false, noBorder = false, width = '100px' }) => ({
  width,
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  display: 'flex',
  justifyContent: hasLabel ? 'space-between' : 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(1),
  boxSizing: 'border-box',
  minHeight: noBorder ? '24px' : '26px',
  backgroundColor: noBorder ? 'transparent' : token.bg,
  border: noBorder ? 'none' : '1px solid',
  borderColor: isOpen ? color.White40_T : color.White08_T,
  borderRadius: '4px',
  color: token.text,
  cursor: 'pointer',
  '&:hover:not(:disabled)': {
    backgroundColor: token.bgSelected,
  },
  '&:disabled': {
    cursor: 'default',
    color: color.White16_T,
    ...(noBorder
      ? {}
      : {
          borderColor: color.White04_T,
        }),
  },
}));

interface DropdownProps<T, Opt extends Option<T>> extends Omit<BaseSingleSelectProps<T, Opt>, 'renderTrigger'> {
  disabled?: boolean;
  emptyState?: ReactNode;
  noBorder?: boolean;
  matchTriggerWidth?: boolean;
  confirmedValue?: Opt | T | null;
  sx?: SxProps;
  width?: string;
  placeholder?: string;
  id?: string;
  /** Caller-defined BI events for opening the menu and confirming a selection. */
  bi?: {
    /**
     * Sends `event` when the menu opens, after `onOpen` returns.
     * `properties` supplies optional static event data. The event is sent without it.
     * Standard analytics metadata is included.
     */
    open?: { event: string; properties?: ApiObject };
    /**
     * Sends `event` when the user confirms an option by pointer or keyboard, after `onChange` returns.
     * `properties` accepts static event data or a function that receives the selected option.
     * The event is sent when `properties` is omitted. Standard analytics metadata is included.
     * Option labels and values are not added automatically.
     */
    select?: { event: string; properties?: ApiObject | ((option: Opt) => ApiObject) };
  };
  fplProps?: {
    position?: DropdownFplPosition;
  };
}

/**
 * FPL `Select` controls trigger height through `size` (`md` = 24px, `lg` = 32px). The legacy MUI
 * trigger height is size-independent — `DropdownButton` renders ~24px for every dropdown regardless of
 * the `size` prop, and only grows when a caller sets `sx.height` (the one `32` case in the app). To
 * keep the FPL trigger height identical to the legacy path, default to `md` and only use `lg` when the
 * caller explicitly asked for a 32px trigger via `sx.height`.
 */
const resolveFplTriggerSize = (sx: SxProps | undefined): 'md' | 'lg' => {
  const rawHeight =
    sx && typeof sx === 'object' && !Array.isArray(sx) ? (sx as { height?: unknown }).height : undefined;
  const height =
    typeof rawHeight === 'number' ? rawHeight : typeof rawHeight === 'string' ? parseInt(rawHeight, 10) : NaN;
  return height === 32 ? 'lg' : 'md';
};

export const Dropdown = <T, Opt extends Option<T>>({
  disabled,
  emptyState = null,
  noBorder = false,
  sx,
  width = '100px',
  value,
  confirmedValue,
  options,
  optionSx: optionSxProp,
  matchTriggerWidth = false,
  placeholder,
  id,
  fplProps,
  bi,
  onOpen,
  onChange,
  ...selectProps
}: DropdownProps<T, Opt>) => {
  const { track } = useAnalytics();
  const handleOpen = useCallback(() => {
    onOpen?.();
    if (bi?.open) {
      track(bi.open.event, bi.open.properties ?? null, TrackTypeEnum.BI);
    }
  }, [onOpen, bi?.open, track]);

  const handleChange = useCallback(
    (option: Opt) => {
      onChange(option);
      if (bi?.select) {
        const properties =
          typeof bi.select.properties === 'function' ?
            bi.select.properties(option) :
            bi.select.properties;
        track(bi.select.event, properties ?? null, TrackTypeEnum.BI);
      }
    },
    [onChange, bi?.select, track],
  );
  const isFplMigrationEnabled = useFplMigrationFlag();
  const theme = useTheme();
  const triggerSx = useDeepEqualMemo(sx);
  const fplTriggerSize = useMemo(() => resolveFplTriggerSize(triggerSx), [triggerSx]);
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(null);
  // Track the trigger width live so `matchTriggerWidth` stays correct when the trigger
  // resizes after the initial measurement. Reading offsetWidth once (e.g. before a modal
  // finishes animating/laying out) otherwise locks the menu to a stale, too-small width.
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!buttonElement) {
      setTriggerWidth(undefined);
      return;
    }
    const measure = () => setTriggerWidth(buttonElement.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(buttonElement);
    return () => observer.disconnect();
  }, [buttonElement]);

  const selectedOption = useMemo(() => {
    if (isOptionLike(value)) return value;
    return options.find((option) => option.value === value);
  }, [value, options]);

  const label = useMemo(() => {
    if (!selectedOption) {
      if (placeholder) {
        return (
          <EllipsisText variant="body-sm-rg" sx={{ lineHeight: '16px' }} color={token.textSecondary}>
            {placeholder}
          </EllipsisText>
        );
      }
      return emptyState;
    }
    if (
      typeof selectedOption.label === 'string' ||
      typeof selectedOption.label === 'number' ||
      typeof selectedOption.label === 'boolean'
    ) {
      let calculatedWidth = width;
      if (!Number.isNaN(Number(calculatedWidth))) {
        calculatedWidth = `${Number(calculatedWidth)}px`;
      }
      // 12px is the width of the caret icon, spacing is the padding of the button
      calculatedWidth = `calc(${calculatedWidth} - ${theme.spacing(1)} - 12px)`;
      return (
        <EllipsisText
          maxWidth={CSS.supports(calculatedWidth) ? calculatedWidth : undefined}
          variant="body-sm-rg"
          sx={{ lineHeight: '16px' }}
          color={disabled ? color.White16_T : token.text}
        >
          {String(selectedOption.label)}
        </EllipsisText>
      );
    }
    return selectedOption.label;
  }, [selectedOption, emptyState, width, theme, disabled, placeholder]);

  const optionSx = useMemo(() => {
    if (!buttonElement) return optionSxProp;
    if (matchTriggerWidth) {
      return {
        ...(optionSxProp || {}),
        // Fill the menu list width (FloatingPanel uses alignItems: flex-start, so the list
        // stretches via alignSelf on BaseSelect; options must be 100% not trigger clientWidth
        // or they stay narrow after the trigger shrinks with a short label).
        width: '100%',
        boxSizing: 'border-box' as const,
      };
    }
    return {
      ...(optionSxProp || {}),
      // 2px is the border of the menu, 2 times spacing is the padding of the menu
      minWidth: `calc(${buttonElement.clientWidth}px - ${theme.spacing(2)} - 2px)`,
      width: '100%',
      maxWidth: selectProps.size === 'large' ? '240px' : '200px',
      boxSizing: 'border-box' as const,
    };
  }, [buttonElement, matchTriggerWidth, optionSxProp, theme, selectProps.size]);

  const menuSx = useMemo(() => {
    if (!matchTriggerWidth || !triggerWidth) return undefined;
    const w = `${triggerWidth}px`;
    return {
      width: w,
      minWidth: w,
      maxWidth: w,
      boxSizing: 'border-box' as const,
    };
  }, [matchTriggerWidth, triggerWidth]);

  const renderTrigger = useCallback(
    ({ isOpen, toggleOpen, triggerRef }: TriggerOptions) => {
      const hasLabel = label !== null && label !== undefined && label !== '';

      return (
        <DropdownButton
          id={id}
          onClick={toggleOpen}
          disabled={disabled}
          hasLabel={hasLabel}
          isOpen={isOpen}
          noBorder={noBorder}
          width={width}
          sx={triggerSx}
          ref={(el) => {
            setButtonElement(el);
            if (triggerRef) {
              (triggerRef as MutableRefObject<HTMLButtonElement | null>).current = el;
            }
          }}
          key="dropdown-button"
        >
          {label}

          <CaretIcon
            style={{
              transition: 'transform 0.2s ease-in-out',
              transform: isOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        </DropdownButton>
      );
    },
    [disabled, noBorder, width, triggerSx, label, id],
  );

  // Flag-on + searchable: the FPL autocomplete-primitive path.
  if (isFplMigrationEnabled && selectProps.search) {
    return (
      <DropdownSearchFpl<T, Opt>
        confirmedValue={confirmedValue}
        disabled={disabled}
        helperText={selectProps.helperText}
        headerAction={selectProps.headerAction}
        searchPlaceholder={selectProps.searchPlaceholder}
        searchInPopup={selectProps.searchInPopup}
        matchTriggerWidth={matchTriggerWidth}
        noOptionsText={selectProps.noOptionsText}
        offset={selectProps.offset}
        onChange={handleChange}
        onClose={selectProps.onClose}
        onHighlightedOptionChange={selectProps.onHighlightedOptionChange}
        onOpen={handleOpen}
        options={options}
        placeholder={placeholder}
        placement={selectProps.placement}
        size={selectProps.size}
        value={value}
        width={width}
      />
    );
  }

  // Flag-on: fixed-list, non-searchable dropdowns render the high-level FPL Select.
  if (isFplMigrationEnabled && !selectProps.search) {
    return (
      <DropdownFpl<T, Opt>
        options={options}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={selectProps.onClose}
        onHighlightedOptionChange={selectProps.onHighlightedOptionChange}
        size={fplTriggerSize}
        value={value}
        confirmedValue={confirmedValue}
        disabled={disabled}
        width={width}
        placeholder={placeholder}
        emptyState={emptyState}
        noOptionsText={selectProps.noOptionsText}
        id={id}
        position={fplProps?.position}
      />
    );
  }

  return (
    <BaseSelect
      {...selectProps}
      onOpen={handleOpen}
      onChange={handleChange}
      value={confirmedValue ?? value}
      options={options}
      renderTrigger={renderTrigger}
      menuSx={menuSx}
      optionSx={optionSx}
    />
  );
};
